import React, { useState } from 'react'
import { useQueryClient } from 'react-query'
import { RoutesManager } from 'utils/routing/routesManager'
import { ApiQueryKeys } from 'api/common/queryKeys'
import { NavigationBreadCrumbs } from 'components/appLayout/appNavigation/breadcrumbs/NavigationBreadCrumbs'
import { Button, ContentGroup, DataGrid, EmptyState } from '@ketch-com/deck'
import { Box, Typography } from '@mui/material'
import { DataCategory } from 'interfaces/dataCategory/dataCategory'
import { useDataCategories } from 'api/dataSystemsClassifications/queries/useDataCategories'
import { PurposeDataCategoriesModal } from 'components/modals/purposes/PurposeDataCategoriesModal'
import { GridRenderCellParams, GridRowParams } from '@mui/x-data-grid-premium'
import { ReactComponent as EmptyStateLabels } from 'assets/icons/emptyState/EmptyStateLabels.svg'
import { useNavigate } from 'react-router-dom'
import { useIsPermitted } from 'utils/hooks'
import { PERMISSIONS } from 'interfaces/permissions/permissions'
import { usePurposeViewContext } from 'pages/policyCenter/purposes/view/context/PurposeViewContext'

/**
 * Data categories tab which allows user to view and update data categories for a purpose.
 */
export const PurposesTabDataCategories: React.FC = () => {
  const { purpose } = usePurposeViewContext()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { data: categories, isLoading: isLoadingCategories } = useDataCategories()
  const reFetchPurpose = () => queryClient.refetchQueries(ApiQueryKeys.purpose)
  const [isMapDataOpen, setIsMapDataOpen] = useState(false)
  const { isPermitted, isPermissionsLoading } = useIsPermitted()
  const isPurposeWriter = isPermitted(PERMISSIONS.PURPOSE_WRITE)
  const isLoading = isLoadingCategories || isPermissionsLoading

  // Data grid config
  const rows = purpose?.dataCategories || []
  const cols = [
    {
      width: 200,
      field: 'name',
      headerName: 'Name',
      renderCell: (params: GridRenderCellParams<(typeof rows)[0]>) => {
        return <Typography variant="label">{params?.row?.name}</Typography>
      },
      sortable: false,
    },
    {
      width: 1015,
      field: 'description',
      headerName: 'Description',
      renderCell: (params: GridRenderCellParams<(typeof rows)[0]>) => {
        return <Typography style={{ whiteSpace: 'pre-wrap' }}>{params?.row?.description}</Typography>
      },
      sortable: false,
    },
  ]

  return (
    <>
      <NavigationBreadCrumbs
        type="dark"
        items={[
          { title: 'Privacy Program', link: RoutesManager.policyCenter.root.getURL() },
          { title: 'Purposes', link: RoutesManager.policyCenter.purposes.root.getURL() },
          {
            title: purpose?.name || purpose?.code,
            link: RoutesManager.policyCenter.purposes.view.root.getURL({ code: purpose?.code }),
          },
          { title: 'Data' },
        ]}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
        <ContentGroup
          title="Data Categories"
          subTitle="The types of data used in the processing for this Purpose."
          actionRightBlockComponent={
            rows.length ? (
              <Button onClick={() => setIsMapDataOpen(true)} sx={{ width: 'fit-content' }} color="tertiary">
                Update Data Categories
              </Button>
            ) : undefined
          }
          titleVariant="h3"
          variant="inner-page-header"
        />

        {/* Data Categories Table */}
        {rows.length ? (
          <Box sx={{ height: '69vh' }}>
            <DataGrid
              getRowId={(row: DataCategory) => row?.ID || ''}
              sx={{
                padding: 'unset',
                cursor: 'pointer',
                '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeaderCheckbox):hover': {
                  backgroundImage: 'unset',
                  cursor: 'auto',
                },
              }}
              rows={rows}
              columns={cols}
              checkboxSelection={false}
              hideFooter={false}
              hideFooterPagination={false}
              hideFooterRowCount
              disableColumnMenu
              disableColumnPinning
              disableColumnReorder
              disableColumnResize
              disableChildrenSorting
              disableRowSelectionOnClick
              loading={false}
              columnMenuProps={
                {
                  slots: {
                    columnMenuFilterItem: null,
                    columnMenuAggregationItem: null,
                    columnMenuGroupingItem: null,
                  },
                } as any
              }
              onRowClick={(params: GridRowParams<DataCategory>) => {
                navigate(RoutesManager.systems.classification.dataCategories.getURL())
              }}
            />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <EmptyState
              customIcon={<EmptyStateLabels />}
              title="Data Categories"
              subTitle="Add data categories to this purpose."
              primaryButtonTitle="Update Data Categories"
              primaryButtonProps={{
                onClick: () => setIsMapDataOpen(true),
                disabled: !isPurposeWriter,
              }}
              primaryButtonTooltipTitle={isPurposeWriter ? '' : 'You do not have permission to create a team.'}
            />
          </Box>
        )}
      </Box>
      {purpose && isMapDataOpen && (
        <PurposeDataCategoriesModal
          purpose={purpose}
          dataCategories={categories?.categories || []}
          isLoading={isLoading}
          onSubmit={() => {
            setIsMapDataOpen(false)
            return reFetchPurpose()
          }}
          onCancel={() => setIsMapDataOpen(false)}
        />
      )}
    </>
  )
}
